interface Point {
  x: number;
  y: number;
}

const degreesToRadians = (degrees: number): number => {
  return (degrees * Math.PI) / 180;
};

/**
 * Convert polar coordinates to cartesian for a TOP-facing semi-circle
 * We use angles where:
 * - 180° = left side of arc
 * - 270° = top of arc (12 o'clock)
 * - 360° (or 0°) = right side of arc
 *
 * In SVG: Y increases downward, so we negate the sin to flip vertically
 */
export const polarToCartesian = (
  centerX: number,
  centerY: number,
  radius: number,
  angleInDegrees: number
): Point => {
  const radians = degreesToRadians(angleInDegrees);
  return {
    x: centerX + radius * Math.cos(radians),
    y: centerY - radius * Math.sin(radians) // Negative to flip Y axis (arc goes UP)
  };
};

/**
 * Generate SVG arc path data for a gauge segment
 * Arc curves upward (above the center point)
 */
export const describeArc = (
  cx: number,
  cy: number,
  radius: number,
  startAngle: number,
  endAngle: number
): string => {
  const start = polarToCartesian(cx, cy, radius, startAngle);
  const end = polarToCartesian(cx, cy, radius, endAngle);

  // For arcs going from higher angle to lower angle (left to right on top semicircle)
  // sweep-flag = 0 draws the shorter arc going counter-clockwise
  const arcSpan = Math.abs(startAngle - endAngle);
  const largeArcFlag = arcSpan > 180 ? 1 : 0;
  const sweepFlag = 1; // clockwise in screen coordinates (because Y is flipped)

  return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} ${sweepFlag} ${end.x} ${end.y}`;
};

/**
 * Map a value from one range to another
 */
export const mapRange = (
  value: number,
  inMin: number,
  inMax: number,
  outMin: number,
  outMax: number
): number => {
  const clamped = Math.max(inMin, Math.min(inMax, value));
  return outMin + ((clamped - inMin) / (inMax - inMin)) * (outMax - outMin);
};

/**
 * Calculate angle for a score value
 * Maps score to angle for top-facing semicircle:
 * - 300 (min) -> 180° (left side)
 * - 850 (max) -> 0° (right side)
 */
export const valueToAngle = (
  value: number,
  minValue: number,
  maxValue: number
): number => {
  return mapRange(value, minValue, maxValue, 180, 0);
};

/**
 * Calculate label position above the arc
 */
export const getLabelPosition = (
  cx: number,
  cy: number,
  radius: number,
  angle: number,
  offset: number = 12
): Point => {
  return polarToCartesian(cx, cy, radius + offset, angle);
};
